/**
 * Review List Modal Component
 * Displays a table of affected users/items for an optimization finding
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FooterActionBar,
  Icon,
  ModalIsland,
  TableSortable,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../../../../shared/hooks';
import type { OptimizationFinding } from '../../type';

import REVIEW_LIST_MODAL_CONSTANTS from './constants';

import './ReviewListModal.css';

export interface ReviewListItem {
  id: string;
  userId: string;
  fullName: string;
  department: string;
  identityType: string;
  current: string;
  status: 'Over-licensed' | 'Dormant' | 'Active' | 'Compliant';
}

export interface AuthObjectField {
  field: string;
  value: string;
}

export interface AuthObjectNode {
  id: string;
  tier: 'Advanced' | 'Core' | 'Dev';
  used: boolean | null;
  fields?: AuthObjectField[];
}

export interface SAPRoleNode {
  id: string;
  authObjects: AuthObjectNode[];
}

export interface AccountNode {
  id: string;
  roles: SAPRoleNode[];
  assignedTier: 'Advanced' | 'Core' | 'Dev';
}

export interface UserTreeNode {
  id: string;
  accounts: AccountNode[];
  assignedTier: 'Advanced' | 'Core' | 'Dev';
  recommendedTier: 'Advanced' | 'Core' | 'Dev';
}

export interface AccountRootNode {
  kind: 'account-root';
  id: string;
  roles: SAPRoleNode[];
  assignedTier: 'Advanced' | 'Core' | 'Dev';
  recommendedTier: 'Advanced' | 'Core' | 'Dev';
}

export type TreeRootNode = UserTreeNode | AccountRootNode;

function isAccountRoot(node: TreeRootNode): node is AccountRootNode {
  return 'kind' in node;
}

export interface ReviewListModalProps {
  isOpen: boolean;
  onClose: () => void;
  finding: OptimizationFinding | null;
  items?: ReviewListItem[];
  treeData?: TreeRootNode[];
  onDeprovision?: () => void;
}

const TIER_CHIP_COLORS: Record<string, 'critical' | 'warning' | 'info'> = {
  Advanced: 'critical',
  Core: 'info',
  Dev: 'warning',
};

interface TreeTableProps {
  data: TreeRootNode[];
  onSelectionChange: (selectedAccountKeys: string[]) => void;
}

function TreeTable({
  data,
  onSelectionChange,
}: TreeTableProps): React.ReactElement {
  const l = useInvertedLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(data.length > 0 ? [data[0].id] : [])
  );
  const [selectedAccountKeys, setSelectedAccountKeys] = useState<Set<string>>(
    new Set()
  );
  const [lastExpandedId, setLastExpandedId] = useState<string | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);

  const filteredData = useMemo<TreeRootNode[]>(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) {
      return data;
    }

    const matchesUsage = (used: boolean | null): boolean => {
      if (used === null) {
        return false;
      }

      const label = used ? 'used' : 'not used';

      return (
        label.startsWith(q) ||
        (!used && 'not used'.includes(q) && q.startsWith('not'))
      );
    };

    const authObjectMatches = (obj: AuthObjectNode): boolean => {
      const fieldsMatch = (obj.fields ?? []).some(
        (fv) =>
          fv.field.toLowerCase().includes(q) ||
          fv.value.toLowerCase().includes(q)
      );

      return (
        obj.id.toLowerCase().includes(q) ||
        obj.tier.toLowerCase().includes(q) ||
        matchesUsage(obj.used) ||
        fieldsMatch
      );
    };

    const filterRoles = (roles: SAPRoleNode[]): SAPRoleNode[] =>
      roles.reduce<SAPRoleNode[]>((racc, role) => {
        if (role.id.toLowerCase().includes(q)) {
          racc.push(role);

          return racc;
        }

        const filteredAuthObjects = role.authObjects.filter(authObjectMatches);

        if (filteredAuthObjects.length > 0) {
          racc.push({ ...role, authObjects: filteredAuthObjects });
        }

        return racc;
      }, []);

    return data.reduce<TreeRootNode[]>((acc, node) => {
      if (isAccountRoot(node)) {
        if (node.id.toLowerCase().includes(q)) {
          acc.push(node);

          return acc;
        }

        const accountRoles = filterRoles(node.roles);

        if (accountRoles.length > 0) {
          acc.push({ ...node, roles: accountRoles });
        }

        return acc;
      }

      if (node.id.toLowerCase().includes(q)) {
        acc.push(node);

        return acc;
      }

      const filteredAccounts = node.accounts.reduce<AccountNode[]>(
        (aacc, account) => {
          if (account.id.toLowerCase().includes(q)) {
            aacc.push(account);

            return aacc;
          }

          const accountRoles = filterRoles(account.roles);

          if (accountRoles.length > 0) {
            aacc.push({ ...account, roles: accountRoles });
          }

          return aacc;
        },
        []
      );

      if (filteredAccounts.length > 0) {
        acc.push({ ...node, accounts: filteredAccounts });
      }

      return acc;
    }, []);
  }, [data, searchQuery]);

  // Auto-expand all matched nodes when searching; reset to first-node-only when cleared
  useEffect(() => {
    if (searchQuery.trim()) {
      const allIds = new Set<string>();

      filteredData.forEach((node) => {
        allIds.add(node.id);

        if (isAccountRoot(node)) {
          node.roles.forEach((role) => {
            allIds.add(`${node.id}-${role.id}`);
          });
        } else {
          node.accounts.forEach((account) => {
            allIds.add(`${node.id}-${account.id}`);
            account.roles.forEach((role) => {
              allIds.add(`${node.id}-${account.id}-${role.id}`);
            });
          });
        }
      });
      setExpanded(allIds);
    } else {
      setExpanded(new Set(data.length > 0 ? [data[0].id] : []));
    }
  }, [searchQuery, filteredData, data]);

  useEffect(() => {
    if (!lastExpandedId || !treeRef.current) {
      return;
    }

    const row = treeRef.current.querySelector(
      `[data-node-id="${lastExpandedId}"]`
    );

    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [lastExpandedId, expanded]);

  const accountKey = (userId: string, accountId: string) =>
    `${userId}__${accountId}`;

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
        setLastExpandedId(null);
      } else {
        next.add(id);
        setLastExpandedId(id);
      }

      return next;
    });
  };

  const isNodeOpen = (id: string) => expanded.has(id);

  const isAccountSelected = (userId: string, accountId: string) =>
    selectedAccountKeys.has(accountKey(userId, accountId));

  const isUserFullySelected = (user: UserTreeNode) =>
    user.accounts.length > 0 &&
    user.accounts.every((a) => isAccountSelected(user.id, a.id));

  const isUserPartiallySelected = (user: UserTreeNode) =>
    user.accounts.some((a) => isAccountSelected(user.id, a.id)) &&
    !isUserFullySelected(user);

  const handleUserSelect = (user: UserTreeNode) => {
    setSelectedAccountKeys((prev) => {
      const next = new Set(prev);
      const allKeys = user.accounts.map((a) => accountKey(user.id, a.id));

      if (isUserFullySelected(user)) {
        allKeys.forEach((k) => next.delete(k));
      } else {
        allKeys.forEach((k) => next.add(k));
      }

      onSelectionChange(Array.from(next));

      return next;
    });
  };

  const totalAuthObjects = filteredData.reduce((sum, node) => {
    if (isAccountRoot(node)) {
      return (
        sum + node.roles.reduce((r, role) => r + role.authObjects.length, 0)
      );
    }

    return (
      sum +
      node.accounts.reduce(
        (acc, account) =>
          acc +
          account.roles.reduce((r, role) => r + role.authObjects.length, 0),
        0
      )
    );
  }, 0);

  const isAccountRootSelected = (node: AccountRootNode) =>
    selectedAccountKeys.has(node.id);

  const handleAccountRootSelect = (node: AccountRootNode) => {
    setSelectedAccountKeys((prev) => {
      const next = new Set(prev);

      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }

      onSelectionChange(Array.from(next));

      return next;
    });
  };

  const renderAuthObjectRows = (
    authObjects: AuthObjectNode[],
    keyPrefix: string,
    shallow = false
  ) => {
    const leafNameClass = `LicenseManagement-ReviewListModal-treeLeaf-name${
      shallow ? ' LicenseManagement-ReviewListModal-treeLeaf-name--shallow' : ''
    }`;

    const rows = authObjects.flatMap((authObj) => {
      const authKey = `${keyPrefix}-${authObj.id}`;
      const fields = authObj.fields ?? [];

      if (fields.length === 0) {
        return [
          <div
            key={authKey}
            className='LicenseManagement-ReviewListModal-treeLeafRow'>
            <div className={leafNameClass}>
              <span className='LicenseManagement-ReviewListModal-treeLabel'>
                {authObj.id}
              </span>
            </div>
            <div className='LicenseManagement-ReviewListModal-treeLeaf-assignedTier' />
            <div className='LicenseManagement-ReviewListModal-treeLeaf-recommendedTier' />
            <div className='LicenseManagement-ReviewListModal-treeLeaf-usage'>
              {authObj.used !== null && (
                <Chip
                  kind='subtleFilled'
                  label={authObj.used ? 'Used' : 'Not Used'}
                  color={authObj.used ? 'success' : 'warning'}
                  size='small'
                />
              )}
            </div>
          </div>,
        ];
      }

      return fields.map((fv, idx) => (
        <div
          key={`${authKey}-${fv.field}-${fv.value}`}
          className={`LicenseManagement-ReviewListModal-treeLeafRow${
            idx > 0
              ? ' LicenseManagement-ReviewListModal-treeLeafRow--fieldContinue'
              : ''
          }`}>
          <div className={leafNameClass}>
            {idx === 0 && (
              <span className='LicenseManagement-ReviewListModal-treeLabel'>
                {authObj.id}
              </span>
            )}
          </div>
          <div className='LicenseManagement-ReviewListModal-treeLeaf-assignedTier'>
            <span className='LicenseManagement-ReviewListModal-treeLeaf-fieldName'>
              {fv.field}
            </span>
          </div>
          <div className='LicenseManagement-ReviewListModal-treeLeaf-recommendedTier'>
            <span className='LicenseManagement-ReviewListModal-treeLeaf-fieldValue'>
              {fv.value}
            </span>
          </div>
          <div className='LicenseManagement-ReviewListModal-treeLeaf-usage'>
            {idx === 0 && authObj.used !== null && (
              <Chip
                kind='subtleFilled'
                label={authObj.used ? 'Used' : 'Not Used'}
                color={authObj.used ? 'success' : 'warning'}
                size='small'
              />
            )}
          </div>
        </div>
      ));
    });

    return rows;
  };

  return (
    <>
      <div className='LicenseManagement-ReviewListModal-controls'>
        <div className='LicenseManagement-ReviewListModal-searchContainer'>
          <Icon
            kind='Search'
            size='small'
            className='LicenseManagement-ReviewListModal-searchIcon'
          />
          <input
            type='text'
            placeholder={l('Search by user, account, role, tier, usage...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='LicenseManagement-ReviewListModal-searchInput'
          />
        </div>
        {/* <Button
          kind='outlined'
          size='small'
          leftIcon={<Icon kind='Filter' />}
          className='LicenseManagement-ReviewListModal-filterButton'>
          {l('Filter')}
        </Button> */}
      </div>

      <p className='LicenseManagement-ReviewListModal-itemCount'>
        {totalAuthObjects} {REVIEW_LIST_MODAL_CONSTANTS.TOTAL_ITEMS}
        {selectedAccountKeys.size > 0 && (
          <span className='LicenseManagement-ReviewListModal-selectionCount'>
            {l(
              ` · ${selectedAccountKeys.size} account${
                selectedAccountKeys.size !== 1 ? 's' : ''
              } selected`
            )}
          </span>
        )}
      </p>

      <div className='LicenseManagement-ReviewListModal-tree' ref={treeRef}>
        {/* Header */}
        <div className='LicenseManagement-ReviewListModal-treeHeader'>
          <div className='LicenseManagement-ReviewListModal-treeHeader-name'>
            {l('NAME')}
          </div>
          <div className='LicenseManagement-ReviewListModal-treeHeader-assignedTier'>
            {l('ASSIGNED TIER')}
          </div>
          <div className='LicenseManagement-ReviewListModal-treeHeader-recommendedTier'>
            {l('RECOMMENDED TIER')}
          </div>
          <div className='LicenseManagement-ReviewListModal-treeHeader-usage' />
        </div>

        {filteredData.length === 0 && (
          <div className='LicenseManagement-ReviewListModal-treeEmpty'>
            {l('No results found.')}
          </div>
        )}

        {filteredData.map((node) =>
          isAccountRoot(node) ? (
            <React.Fragment key={node.id}>
              {/* Account root row — Account → Role → AuthObject */}
              <div
                data-node-id={node.id}
                className='LicenseManagement-ReviewListModal-treeRow LicenseManagement-ReviewListModal-treeRow--level0'
                onClick={() => toggle(node.id)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggle(node.id)}>
                <div className='LicenseManagement-ReviewListModal-treeRow-nameGroup'>
                  <div
                    role='presentation'
                    className='LicenseManagement-ReviewListModal-treeCheckbox'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAccountRootSelect(node);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleAccountRootSelect(node);
                      }
                    }}>
                    <Checkbox
                      kind='base'
                      isChecked={isAccountRootSelected(node)}
                      onChange={() => {}}
                      ariaLabel={`Select ${node.id}`}
                    />
                  </div>
                  <Icon
                    kind={isNodeOpen(node.id) ? 'ChevronDown' : 'ChevronRight'}
                    size='xSmall'
                    className='LicenseManagement-ReviewListModal-treeChevron'
                  />
                  <span className='LicenseManagement-ReviewListModal-treeLabel LicenseManagement-ReviewListModal-treeLabel--bold'>
                    {node.id}
                  </span>
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-assignedTier'>
                  <Chip
                    kind='subtleFilled'
                    label={node.assignedTier}
                    color={TIER_CHIP_COLORS[node.assignedTier]}
                    size='small'
                  />
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-recommendedTier'>
                  <Chip
                    kind='subtleFilled'
                    label={node.recommendedTier}
                    color={TIER_CHIP_COLORS[node.recommendedTier]}
                    size='small'
                  />
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-usage' />
              </div>

              {isNodeOpen(node.id) &&
                node.roles.map((role, roleIndex) => {
                  const roleKey = `${node.id}-${role.id}`;
                  const isFirstExpandedRole =
                    roleIndex ===
                    node.roles.findIndex((r) =>
                      isNodeOpen(`${node.id}-${r.id}`)
                    );

                  return (
                    <React.Fragment key={roleKey}>
                      {/* Role row */}
                      <div
                        data-node-id={roleKey}
                        className='LicenseManagement-ReviewListModal-treeRow LicenseManagement-ReviewListModal-treeRow--level1'
                        onClick={() => toggle(roleKey)}
                        role='button'
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggle(roleKey)}>
                        <div className='LicenseManagement-ReviewListModal-treeRow-nameGroup'>
                          <Icon
                            kind={
                              isNodeOpen(roleKey)
                                ? 'ChevronDown'
                                : 'ChevronRight'
                            }
                            size='xSmall'
                            className='LicenseManagement-ReviewListModal-treeChevron'
                          />
                          <span className='LicenseManagement-ReviewListModal-treeLabel'>
                            {role.id}
                          </span>
                        </div>
                        <div className='LicenseManagement-ReviewListModal-treeRow-assignedTier'>
                          {isNodeOpen(roleKey) && isFirstExpandedRole && (
                            <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                              {l('FIELD')}
                            </span>
                          )}
                        </div>
                        <div className='LicenseManagement-ReviewListModal-treeRow-recommendedTier'>
                          {isNodeOpen(roleKey) && isFirstExpandedRole && (
                            <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                              {l('VALUE')}
                            </span>
                          )}
                        </div>
                        <div className='LicenseManagement-ReviewListModal-treeRow-usage'>
                          {isNodeOpen(roleKey) && isFirstExpandedRole && (
                            <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                              {l('USAGE')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Auth object leaf rows with inline sub-header */}
                      {isNodeOpen(roleKey) &&
                        renderAuthObjectRows(role.authObjects, roleKey, true)}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ) : (
            <React.Fragment key={node.id}>
              {/* User row */}
              <div
                data-node-id={node.id}
                className='LicenseManagement-ReviewListModal-treeRow LicenseManagement-ReviewListModal-treeRow--level0'
                onClick={() => toggle(node.id)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggle(node.id)}>
                <div className='LicenseManagement-ReviewListModal-treeRow-nameGroup'>
                  <div
                    role='presentation'
                    className='LicenseManagement-ReviewListModal-treeCheckbox'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserSelect(node);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleUserSelect(node);
                      }
                    }}>
                    <Checkbox
                      kind='base'
                      isChecked={isUserFullySelected(node)}
                      isIndeterminate={isUserPartiallySelected(node)}
                      onChange={() => {}}
                      ariaLabel={`Select ${node.id}`}
                    />
                  </div>
                  <Icon
                    kind={isNodeOpen(node.id) ? 'ChevronDown' : 'ChevronRight'}
                    size='xSmall'
                    className='LicenseManagement-ReviewListModal-treeChevron'
                  />
                  <span className='LicenseManagement-ReviewListModal-treeLabel LicenseManagement-ReviewListModal-treeLabel--bold'>
                    {node.id}
                  </span>
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-assignedTier'>
                  <Chip
                    kind='subtleFilled'
                    label={node.assignedTier}
                    color={TIER_CHIP_COLORS[node.assignedTier]}
                    size='small'
                  />
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-recommendedTier'>
                  <Chip
                    kind='subtleFilled'
                    label={node.recommendedTier}
                    color={TIER_CHIP_COLORS[node.recommendedTier]}
                    size='small'
                  />
                </div>
                <div className='LicenseManagement-ReviewListModal-treeRow-usage' />
              </div>

              {isNodeOpen(node.id) &&
                node.accounts.map((account) => (
                  <React.Fragment key={`${node.id}-${account.id}`}>
                    {/* Account row */}
                    <div
                      data-node-id={`${node.id}-${account.id}`}
                      className='LicenseManagement-ReviewListModal-treeRow LicenseManagement-ReviewListModal-treeRow--level1'
                      onClick={() => toggle(`${node.id}-${account.id}`)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && toggle(`${node.id}-${account.id}`)
                      }>
                      <div className='LicenseManagement-ReviewListModal-treeRow-nameGroup'>
                        <Icon
                          kind={
                            isNodeOpen(`${node.id}-${account.id}`)
                              ? 'ChevronDown'
                              : 'ChevronRight'
                          }
                          size='xSmall'
                          className='LicenseManagement-ReviewListModal-treeChevron'
                        />
                        <span className='LicenseManagement-ReviewListModal-treeLabel'>
                          {account.id}
                        </span>
                      </div>
                      <div className='LicenseManagement-ReviewListModal-treeRow-assignedTier'>
                        <Chip
                          kind='subtleFilled'
                          label={account.assignedTier}
                          color={TIER_CHIP_COLORS[account.assignedTier]}
                          size='small'
                        />
                      </div>
                      <div className='LicenseManagement-ReviewListModal-treeRow-recommendedTier' />
                      <div className='LicenseManagement-ReviewListModal-treeRow-usage' />
                    </div>

                    {isNodeOpen(`${node.id}-${account.id}`) &&
                      account.roles.map((role, roleIndex) => {
                        const roleKey = `${node.id}-${account.id}-${role.id}`;
                        const isFirstExpandedRole =
                          roleIndex ===
                          account.roles.findIndex((r) =>
                            isNodeOpen(`${node.id}-${account.id}-${r.id}`)
                          );

                        return (
                          <React.Fragment key={roleKey}>
                            {/* Role row */}
                            <div
                              data-node-id={roleKey}
                              className='LicenseManagement-ReviewListModal-treeRow LicenseManagement-ReviewListModal-treeRow--level2'
                              onClick={() => toggle(roleKey)}
                              role='button'
                              tabIndex={0}
                              onKeyDown={(e) =>
                                e.key === 'Enter' && toggle(roleKey)
                              }>
                              <div className='LicenseManagement-ReviewListModal-treeRow-nameGroup'>
                                <Icon
                                  kind={
                                    isNodeOpen(roleKey)
                                      ? 'ChevronDown'
                                      : 'ChevronRight'
                                  }
                                  size='xSmall'
                                  className='LicenseManagement-ReviewListModal-treeChevron'
                                />
                                <span className='LicenseManagement-ReviewListModal-treeLabel'>
                                  {role.id}
                                </span>
                              </div>
                              <div className='LicenseManagement-ReviewListModal-treeRow-assignedTier'>
                                {isNodeOpen(roleKey) && isFirstExpandedRole && (
                                  <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                                    {l('FIELD')}
                                  </span>
                                )}
                              </div>
                              <div className='LicenseManagement-ReviewListModal-treeRow-recommendedTier'>
                                {isNodeOpen(roleKey) && isFirstExpandedRole && (
                                  <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                                    {l('VALUE')}
                                  </span>
                                )}
                              </div>
                              <div className='LicenseManagement-ReviewListModal-treeRow-usage'>
                                {isNodeOpen(roleKey) && isFirstExpandedRole && (
                                  <span className='LicenseManagement-ReviewListModal-treeRow-expandedLabel'>
                                    {l('USAGE')}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Auth object leaf rows with inline sub-header */}
                            {isNodeOpen(roleKey) &&
                              renderAuthObjectRows(
                                role.authObjects,
                                roleKey,
                                false
                              )}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                ))}
            </React.Fragment>
          )
        )}
      </div>
    </>
  );
}

/**
 * Displays a modal with a tree or sortable table of affected users/items
 * for an optimization finding. Provides deprovision action via footer buttons.
 */
function ReviewListModal({
  isOpen,
  onClose,
  finding,
  items = [],
  treeData = undefined,
  onDeprovision = undefined,
}: ReviewListModalProps): React.ReactElement | null {
  const localize = useInvertedLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccountKeys, setSelectedAccountKeys] = useState<string[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const footerWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!footerWrapperRef.current) {
      return;
    }

    const footerBar = footerWrapperRef.current.querySelector<HTMLElement>(
      '[class*="FooterActionBar"]'
    );

    if (footerBar) {
      footerBar.style.padding = isConfirming ? '0' : '';
    }
  }, [isConfirming]);

  const titleLower = finding?.title.toLowerCase() ?? '';
  const isDeprovision = titleLower.startsWith('deprovision');
  const isDowngrade = titleLower.startsWith('downgrade');

  const modalSubtitle: string = (() => {
    if (isDeprovision) {
      return REVIEW_LIST_MODAL_CONSTANTS.SUBTITLE_DEPROVISION;
    }

    if (isDowngrade) {
      return REVIEW_LIST_MODAL_CONSTANTS.SUBTITLE_DOWNGRADE;
    }

    return REVIEW_LIST_MODAL_CONSTANTS.SUBTITLE_DEFAULT;
  })();

  const actionButtonLabel: string = (() => {
    if (isDeprovision) {
      return REVIEW_LIST_MODAL_CONSTANTS.BUTTON_DEPROVISION;
    }

    if (isDowngrade) {
      return REVIEW_LIST_MODAL_CONSTANTS.BUTTON_DOWNGRADE;
    }

    return REVIEW_LIST_MODAL_CONSTANTS.BUTTON_APPLY;
  })();

  const confirmMessage: string = (() => {
    const count = selectedAccountKeys.length;

    if (isDeprovision) {
      if (count === 0) {
        return String(REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DEPROVISION_ALL);
      }

      if (count === 1) {
        return String(REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DEPROVISION_SINGLE);
      }

      return String(
        REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DEPROVISION_MULTI
      ).replace('{count}', String(count));
    }

    if (isDowngrade) {
      if (count === 0) {
        return String(REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DOWNGRADE_ALL);
      }

      if (count === 1) {
        return String(REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DOWNGRADE_SINGLE);
      }

      return String(
        REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DOWNGRADE_MULTI
      ).replace('{count}', String(count));
    }

    return String(REVIEW_LIST_MODAL_CONSTANTS.CONFIRM_DEFAULT);
  })();

  const [columns, setColumns] = useState([
    {
      key: 'userId',
      label: 'USER ID',
      value: 'userId',
      isSortable: true,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '120px',
    },
    {
      key: 'fullName',
      label: 'FULL NAME',
      value: 'fullName',
      isSortable: true,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '140px',
    },
    {
      key: 'department',
      label: 'DEPARTMENT',
      value: 'department',
      isSortable: true,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '130px',
    },
    {
      key: 'identityType',
      label: 'IDENTITY TYPE',
      value: 'identityType',
      isSortable: false,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '130px',
    },
    {
      key: 'current',
      label: 'CURRENT',
      value: 'current',
      isSortable: false,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '110px',
    },
    {
      key: 'status',
      label: 'STATUS',
      value: 'status',
      isSortable: false,
      isChecked: true,
      isPinned: false,
      isDisabled: false,
      width: '120px',
    },
  ]);

  if (!isOpen || !finding) {
    return null;
  }

  const getStatusChipColor = (
    status: string
  ): 'success' | 'warning' | 'critical' | 'info' => {
    switch (status) {
      case 'Over-licensed':
        return 'critical';
      case 'Dormant':
        return 'warning';
      case 'Active':
        return 'success';
      case 'Compliant':
        return 'success';
      default:
        return 'info';
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.userId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortOptions = [
    {
      label: 'Ascending',
      value: 'asc',
      isDisabled: false,
      isChecked: false,
      icon: 'ArrowDown',
      shouldShowCheckIcon: true,
    },
    {
      label: 'Descending',
      value: 'desc',
      isDisabled: false,
      isChecked: false,
      icon: 'ArrowUp',
      shouldShowCheckIcon: true,
    },
  ];

  const sortConfigs = columns
    .filter((col) => col.isSortable)
    .map((col) => ({ key: col.key, sortOptions }));

  const rows = filteredItems.map((item) => ({
    id: item.id,
    userId: item.userId,
    fullName: item.fullName,
    department: item.department,
    identityType: item.identityType,
    current: item.current,
    status: (
      <Chip
        kind='subtleFilled'
        label={item.status}
        color={getStatusChipColor(item.status)}
        size='small'
      />
    ),
  }));

  const isActionDisabled = treeData ? selectedAccountKeys.length === 0 : false;

  const handleActionClick = () => {
    setIsConfirming(true);
  };

  const handleConfirm = () => {
    setIsConfirming(false);
    onDeprovision?.();
    onClose();
  };

  const handleGoBack = () => {
    setIsConfirming(false);
  };

  const contentBody = (
    <div className='LicenseManagement-ReviewListModal-content'>
      {treeData ? (
        <TreeTable data={treeData} onSelectionChange={setSelectedAccountKeys} />
      ) : (
        <>
          <div className='LicenseManagement-ReviewListModal-controls'>
            <div className='LicenseManagement-ReviewListModal-searchContainer'>
              <Icon
                kind='Search'
                size='small'
                className='LicenseManagement-ReviewListModal-searchIcon'
              />
              <input
                type='text'
                placeholder='Search by name, user ID, etc...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='LicenseManagement-ReviewListModal-searchInput'
              />
            </div>
            <Button
              kind='outlined'
              size='small'
              leftIcon={<Icon kind='Filter' />}
              className='LicenseManagement-ReviewListModal-filterButton'>
              {localize('Filter')}
            </Button>
          </div>

          <p className='LicenseManagement-ReviewListModal-itemCount'>
            {filteredItems.length} {REVIEW_LIST_MODAL_CONSTANTS.TOTAL_ITEMS}
          </p>

          <div className='LicenseManagement-ReviewListModal-tableWrapper'>
            <TableSortable
              columns={columns}
              setColumns={setColumns}
              data={rows}
              sortConfigs={sortConfigs}
              isWindowScroll={false}
              tableHeight='350px'
              columnConfigs={{
                isDndDisabled: true,
                isHeaderSticky: true,
                isHorizontalScroll: true,
              }}
            />
          </div>
        </>
      )}
    </div>
  );

  const footer = (
    <div ref={footerWrapperRef}>
      <FooterActionBar
        size='large'
        className='LicenseManagement-ReviewListModal-footerBar'>
        {isConfirming ? (
          <div className='LicenseManagement-ReviewListModal-confirmStrip'>
            <div className='LicenseManagement-ReviewListModal-confirmMessage'>
              <Icon
                kind='AlertWarningFilled'
                size='small'
                className='LicenseManagement-ReviewListModal-confirmIcon'
              />
              <span className='LicenseManagement-ReviewListModal-confirmText'>
                {confirmMessage}
              </span>
            </div>
            <div className='LicenseManagement-ReviewListModal-confirmActions'>
              <Button
                kind='ghost'
                onClick={handleGoBack}
                className='LicenseManagement-ReviewListModal-goBackButton'>
                {localize('Go Back')}
              </Button>
              <Button
                kind='filled'
                onClick={handleConfirm}
                className='LicenseManagement-ReviewListModal-confirmButton'>
                {localize(`Confirm ${actionButtonLabel}`)}
              </Button>
            </div>
          </div>
        ) : (
          <div className='LicenseManagement-ReviewListModal-footer'>
            <Button
              kind='ghost'
              onClick={onClose}
              className='LicenseManagement-ReviewListModal-cancelButton'>
              {localize('Cancel')}
            </Button>
            <Button
              kind='filled'
              onClick={handleActionClick}
              isDisabled={isActionDisabled}
              className='LicenseManagement-ReviewListModal-deprovisionButton'>
              {localize(actionButtonLabel)}
              {selectedAccountKeys.length > 0 &&
                localize(` (${selectedAccountKeys.length})`)}
            </Button>
          </div>
        )}
      </FooterActionBar>
    </div>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      title={REVIEW_LIST_MODAL_CONSTANTS.REVIEW_LIST}
      subtitle={modalSubtitle}
      contentBody={contentBody}
      FooterActionBarComp={footer}
      dataTheme='dark'
      overlayBackgroundColor='rgba(128, 128, 128, 0.5)'
      className='LicenseManagement-ReviewListModal'
    />
  );
}

ReviewListModal.defaultProps = {
  items: [],
  onDeprovision: () => {},
  treeData: undefined,
};

export default ReviewListModal;
